import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useSlackCRUDActions } from '@mm/state/actions/SlackCRUDActions';
import { SlideOutMenuType } from '@mm/state/interfaces/view';
import ConnectionModel from '@mm/state/model/ConnectionModel';
import { selectSlackConnections } from '@mm/state/modules/connectionsTableState/selectors';
import { openNewConfirmModal } from '@mm/state/modules/modalsState/actions';
import { selectSlackAccounts } from '@mm/state/selectors/slackSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { errorToast, successToast } from '../../../../../common/helpers/toast';

import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import SlackNotificationHelper from './schema-helper';
import { SlackNotificationEditor } from './SlackNotificationEditor/SlackNotificationEditor';

export interface SlackNotificationSlideoutEditorProps {
  menuType: SlideOutMenuType;
  widgetId: number;
  onClose?: () => void;
}

export const SlackNotificationSlideoutEditor = ({
  widgetId,
  menuType,
  onClose: onCloseProp = () => {},
}: SlackNotificationSlideoutEditorProps) => {
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const slackConnections = useSelector(selectSlackConnections) ?? [];

  const { create, update } = useConnectionCRUDActions();
  const { getAccounts } = useSlackCRUDActions();
  const accounts = useSelector(selectSlackAccounts);
  const [connection, setConnection] = useState<ConnectionModel>(
    slackConnections.find(c => c.id === widgetId) || slackConnections[0],
  );

  const [hasChanged, setHasChanged] = useState(false);

  useEffect(() => {
    const getConnection = async () => {
      let newConnection;

      if (!accounts.length) {
        await getAccounts(activeFbPageId);
      }

      if (!connection) {
        newConnection = await create(activeFbPageId, {
          name: 'Slack Connection',
          connectionKind: 'slack',
          slackParams: { accessTokenId: accounts[0].id },
        });

        setConnection(newConnection);
      }
    };

    getConnection();
  }, []);

  const [busy, setBusy] = useState(false);
  const [isTestSuccess, setIsTestSuccess] = useState<boolean | undefined>(
    undefined,
  );
  const { sendConnectionTest } = useSlackCRUDActions();

  const formik = useFormik<ConnectionModel>({
    initialValues: connection,
    validate: SlackNotificationHelper.validate(),
    onSubmit: async () => {},
  });

  const valuesRoutes = {
    teamId: 'slackAccessToken.teamId',
    slackChannelId: 'data.listId',
    slackChannelName: 'data.listName',
    assetUrl: 'data.messageInfo.assetUrl',
    content: 'data.messageInfo.content',
    sendAsBot: 'data.messageInfo.sendAsBot',
    botName: 'data.messageInfo.botName',
    audienceId: 'audienceId',
    accountId: 'slackParams.accessTokenId',
  };

  const getField = (fieldName: string) =>
    valuesRoutes[fieldName].split('.').reduce((p, c) => p?.[c], formik.values);

  const setField = (fieldName: string, value: any) => {
    if (getField(fieldName) !== value) {
      setHasChanged(true);
      formik.setFieldValue(`${valuesRoutes[fieldName]}`, value);
    }
  };

  const onSave = async (skipClose?: boolean) => {
    setBusy(true);
    try {
      if (connection) {
        await update(activeFbPageId, {
          ...connection,
          ...formik.values,
          active: true,
        });

        successToast('Your changes have been saved.', 'top-left');

        if (!skipClose) {
          closeSlideout();
        }
      }
    } catch (ee) {
      errorToast('Something went wrong', 'top-left');
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    try {
      if (!connection.slackAccessToken) {
        return;
      }

      if (hasChanged) {
        await onSave(true);
      }
      const res = await sendConnectionTest(
        connection.id,
        activeFbPageId,
        connection.slackAccessToken.id,
      );
      if (res.success) {
        setIsTestSuccess(true);
      }
    } catch (e) {
      setIsTestSuccess(false);
    }
  };

  const onBeforeClose = () => {
    if (!hasChanged) {
      return Promise.resolve(true);
    }

    return new Promise((resolve, reject) => {
      openNewConfirmModal({
        title: 'You have unsaved changes',
        subtitle: 'Are you sure you want to leave without saving?',
        onReject: async () => reject(),
        onResolve: async close => {
          close();
          resolve(true);
        },
        resolveButton: {
          children: "Yes, I'm sure",
        },
      });
    });
  };

  const close = async () => {
    try {
      await onBeforeClose();
      closeSlideout();
      onCloseProp();
    } catch (err) {}
  };

  const { setRef, close: closeSlideout } = useSlideoutOnClose(
    onBeforeClose,
    menuType,
  );

  return (
    <SlackNotificationEditor
      formik={formik}
      setField={setField}
      getField={getField}
      onSave={onSave}
      test={test}
      close={close}
      busy={busy}
      setRef={setRef}
      isTestSuccess={isTestSuccess}
      setIsTestSuccess={setIsTestSuccess}
      withAudienceSelect={true}
    />
  );
};
